import React, { useEffect, useRef } from "react";
import cn from "classnames";
import { CardEvent } from "features/shared/OpenedCardEvents/types";
import { appStore } from "entities/App";
import { authStore } from "entities/Auth";
import { useOpenedCardSearchQuery } from "entities/ParcelSearchCard";
import { subscriptionStore } from "entities/Subscription";
import { useDebounce } from "shared/hooks/useDebounce.hook";
import { useForceRender } from "shared/hooks/useForceRender.hook";
import { useResize } from "shared/hooks/useResize.hook";
import { useAppSelector } from "shared/lib/redux";
import { ArrowDownIcon } from "shared/ui/Icons/Icons";
import styles from "./Event.module.scss";
const defaultSubeventInfoHeight = "14px";

const getTimeBoundClassName = (isFutureEvent: boolean, type: "event" | "subevent"): string => {
  return isFutureEvent ? styles[`is-future-${type}`] : styles[`is-present-${type}`];
};

type EventsProps = {
  className?: string;
  eventEntry: CardEvent;
  isLastEvent: boolean;
};

export const Event: React.FC<EventsProps> = ({ isLastEvent, eventEntry, className }) => {
  const heightRef = useRef<Record<number, number | undefined>>({});
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const openedCardSearchRequestMeta = useOpenedCardSearchQuery({
    isAuthenticated,
    setSubscription: (subscription) => ({
      type: subscriptionStore.actions.setSubscription.type,
      payload: subscription,
    }),
    setActivePaymentModal: (activePaymentModal) => ({
      type: subscriptionStore.actions.setActivePaymentModal.type,
      payload: activePaymentModal,
    }),
    setAppIntegrationRequestLimitReached: (appIntegrationRequestLimitReached) => ({
      type: appStore.actions.setAppIntegrationRequestLimitReached.type,
      payload: appIntegrationRequestLimitReached,
    }),
    setWebAppRequestLimitReached: (webAppRequestLimitReached) => ({
      type: appStore.actions.setWebAppRequestLimitReached.type,
      payload: webAppRequestLimitReached,
    }),
  });

  const { forceRender } = useForceRender();
  const { debounce } = useDebounce(150);

  useResize(() =>
    debounce(() => {
      forceRender(); // need for date height
    })
  );

  useEffect(() => {
    if (openedCardSearchRequestMeta.isSearchSuccess) {
      forceRender(); // need for date height
    }
  }, [openedCardSearchRequestMeta.isSearchSuccess]);

  const { location, subEvents, isFutureEvent } = eventEntry;

  return (
    <div className={cn(styles["event-entry"], getTimeBoundClassName(isFutureEvent, "event"), className)}>
      <div className={styles.name}>{location}</div>
      <div className={styles["events-box"]}>
        {subEvents.descriptionList.map((cardEventDescription, index) => {
          const isLastSubeventWithinEntry = subEvents.descriptionList.length - 1 === index;
          return (
            <div
              key={index}
              ref={(e) => {
                heightRef.current[index] = e?.getBoundingClientRect().height;
              }}
              className={cn(
                styles["subevent-info"],
                styles["description"],
                isLastSubeventWithinEntry && styles["is-last-subevent-within-entry"],
                cardEventDescription.isTotalLastPresentSubevent && styles["is-total-last-present-subevent"],
                getTimeBoundClassName(cardEventDescription.isFutureSubevent, "subevent"),
                isLastEvent && styles["is-last-event"]
              )}
            >
              {cardEventDescription.value}
              {cardEventDescription.isTotalLastPresentSubevent && (
                <ArrowDownIcon className={styles["subevent-arrow"]} />
              )}
            </div>
          );
        })}
      </div>
      <div className={styles["events-box"]}>
        {subEvents.dateList.map((cardEventDate, index) => {
          const isLastSubeventWithinEntry = subEvents.dateList.length - 1 === index;
          const height = heightRef.current[index] ? `${heightRef.current[index]}px` : defaultSubeventInfoHeight;
          return (
            <div
              key={index}
              style={{ height }}
              className={cn(
                styles["subevent-info"],
                isLastSubeventWithinEntry && styles["is-last-subevent-within-entry"],
                cardEventDate.isTotalLastPresentSubevent && styles["is-total-last-present-subevent"],
                getTimeBoundClassName(cardEventDate.isFutureSubevent, "subevent"),
                isLastEvent && styles["is-last-event"]
              )}
            >
              {cardEventDate.value}
            </div>
          );
        })}
      </div>
    </div>
  );
};
